<template>
  <span v-if="hidden" class="inline-flex items-center text-[11px] text-gray-400">—</span>
  <span v-else class="relative inline-flex">
    <button
      type="button"
      ref="trigger"
      @click.stop="toggleOpen"
      :class="[
        'inline-flex items-center gap-0.5 text-[11px] font-medium tabular-nums rounded px-1 -mx-1 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-gray-300 cursor-pointer transition-colors',
        isFlat ? 'text-gray-400' : (isUp ? 'text-emerald-600' : 'text-rose-600')
      ]"
    >
      <span v-if="!isFlat" class="inline-block leading-none">{{ isUp ? '↑' : '↓' }}</span>
      {{ pct }}%
    </button>

    <Teleport to="body">
      <div
        v-if="open"
        ref="pop"
        class="fixed z-[700] bg-white rounded-lg shadow-lg border border-gray-200 p-3 w-56 animate-fade-in"
        :style="popStyle"
        @click.stop
      >
        <div class="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Period comparison</div>
        <div class="space-y-1.5">
          <div class="flex items-baseline justify-between gap-2">
            <span class="text-xs text-gray-500">Current</span>
            <span class="text-sm font-data font-medium text-gray-900">{{ fmt(current) }}</span>
          </div>
          <div class="flex items-baseline justify-between gap-2">
            <span class="text-xs text-gray-500">Previous</span>
            <span class="text-sm font-data text-gray-700">{{ fmt(previous) }}</span>
          </div>
          <div class="border-t border-gray-100 my-1" />
          <div class="flex items-baseline justify-between gap-2">
            <span class="text-xs text-gray-500">Change</span>
            <span
              :class="[
                'text-sm font-data font-medium text-right',
                isFlat ? 'text-gray-500' : (isUp ? 'text-emerald-600' : 'text-rose-600')
              ]"
            >{{ deltaSign }}{{ fmt(Math.abs((current || 0) - (previous || 0))) }}</span>
          </div>
          <div class="flex items-baseline justify-between gap-2">
            <span class="text-xs text-gray-500">Percent</span>
            <span
              :class="[
                'text-sm font-data font-medium',
                isFlat ? 'text-gray-500' : (isUp ? 'text-emerald-600' : 'text-rose-600')
              ]"
            >{{ isFlat ? '' : (deltaSign) }}{{ pct }}%</span>
          </div>
        </div>
      </div>
    </Teleport>
  </span>
</template>

<script>
/**
 * Delta indicator. Renders `↑ 12%` (green) or `↓ 8%` (red) given a
 * current and previous numeric pair. Falls back to a neutral em-dash
 * when comparison is meaningless:
 *   - prev is null/undefined (request didn't include prev period)
 *   - prev is 0 AND current is 0 (no change from nothing)
 *   - prev is 0 AND current > 0 ("∞%" — not useful, would dominate)
 *
 * Click the badge to open a small popover with the current value,
 * previous value, and the absolute / percent change. Pass a
 * `formatter` function so the popover renders values in the same
 * units as the surrounding context (counts, durations, bytes...).
 *
 * Used by both KPI cards (per stat) and Top Content rows (Streams
 * column only). Inverse-colored prop flips green/red for metrics
 * where down is good (Errors, Buffering Events, Bounces).
 */
export default {
  name: 'DeltaBadge',
  props: {
    current:  { type: Number, default: 0 },
    previous: { type: Number, default: null },
    // When true, "decrease" renders green and "increase" renders
    // red. Use for metrics like Errors / Buffering where going down
    // is the desirable direction.
    inverse:  { type: Boolean, default: false },
    // Optional value formatter shared with the surrounding context
    // (e.g. formatNumber, formatDurationMs). Defaults to localized
    // integer formatting if not provided.
    formatter: { type: Function, default: null }
  },
  data() {
    return {
      open: false,
      popStyle: {}
    }
  },
  computed: {
    hidden() {
      if (this.previous == null) return true
      if (!this.previous) return true
      return false
    },
    rawDelta() {
      return ((this.current || 0) - (this.previous || 0)) / this.previous * 100
    },
    isFlat() {
      return Math.round(this.rawDelta * 10) === 0
    },
    pct() {
      const abs = Math.abs(this.rawDelta)
      if (abs < 1 && abs > 0) return abs.toFixed(1)
      return Math.round(abs)
    },
    isUp() {
      const goingUp = (this.current || 0) > (this.previous || 0)
      return this.inverse ? !goingUp : goingUp
    },
    deltaSign() {
      if (this.isFlat) return ''
      // Sign reflects raw movement, not inverse-colored direction —
      // a decrease in errors is still a "-N" change.
      return (this.current || 0) >= (this.previous || 0) ? '+' : '-'
    }
  },
  beforeUnmount() {
    this.unbindDismiss()
  },
  methods: {
    fmt(v) {
      if (v == null) return '—'
      if (this.formatter) return this.formatter(v)
      try { return Number(v).toLocaleString() } catch { return String(v) }
    },
    toggleOpen() {
      if (this.open) {
        this.close()
        return
      }
      this.open = true
      this.$nextTick(() => {
        this.positionPopover()
        this.bindDismiss()
      })
    },
    close() {
      this.open = false
      this.unbindDismiss()
    },
    positionPopover() {
      const trigger = this.$refs.trigger
      const pop = this.$refs.pop
      if (!trigger || !pop) return
      const r = trigger.getBoundingClientRect()
      const popW = pop.offsetWidth || 224
      const popH = pop.offsetHeight || 110
      const vw = window.innerWidth
      const vh = window.innerHeight
      let left = r.left + r.width / 2 - popW / 2
      left = Math.max(8, Math.min(vw - popW - 8, left))
      let top = r.bottom + 6
      if (top + popH > vh - 8) top = r.top - popH - 6
      this.popStyle = { left: left + 'px', top: top + 'px' }
    },
    bindDismiss() {
      this._onDocClick = (e) => {
        const trigger = this.$refs.trigger
        const pop = this.$refs.pop
        if (trigger && trigger.contains(e.target)) return
        if (pop && pop.contains(e.target)) return
        this.close()
      }
      this._onEsc = (e) => { if (e.key === 'Escape') this.close() }
      this._onScroll = () => this.close()
      document.addEventListener('mousedown', this._onDocClick)
      document.addEventListener('keydown', this._onEsc)
      window.addEventListener('scroll', this._onScroll, true)
      window.addEventListener('resize', this._onScroll)
    },
    unbindDismiss() {
      if (this._onDocClick) document.removeEventListener('mousedown', this._onDocClick)
      if (this._onEsc) document.removeEventListener('keydown', this._onEsc)
      if (this._onScroll) {
        window.removeEventListener('scroll', this._onScroll, true)
        window.removeEventListener('resize', this._onScroll)
      }
      this._onDocClick = null
      this._onEsc = null
      this._onScroll = null
    }
  }
}
</script>
